import { useEffect, useRef, useId, type ReactNode } from 'react';
import { X, ArrowUpRight, LoaderCircle, CircleCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export function useApi<T>(path: string, enabled = true) {
  return useQuery<T>({ queryKey: ['api', path], queryFn: () => api<T>(path), enabled });
}
export function Spinner() {
  return <LoaderCircle className="spin" size={18} aria-label="加载中" />;
}
export function Empty({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon ?? <ArrowUpRight size={26} />}</div>
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}
export function Dialog({
  title,
  subtitle,
  children,
  close,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  close: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="dialog"
      onCancel={close}
      onClick={(event) => {
        if (event.target === ref.current) close();
      }}
      aria-labelledby={titleId}
    >
      <header>
        <div>
          <h2 id={titleId}>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button type="button" className="icon-button" onClick={close} aria-label="关闭">
          <X size={20} />
        </button>
      </header>
      {children}
    </dialog>
  );
}
export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'green' | 'amber' | 'blue';
}) {
  return (
    <span className={`badge ${tone}`}>
      {tone === 'green' && <CircleCheck size={11} />} {children}
    </span>
  );
}
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function MoonScene({ small = false }: { small?: boolean }) {
  return (
    <div className={`moon-scene ${small ? 'small' : ''}`} aria-hidden="true">
      <div className="orbital orbit-one" />
      <div className="orbital orbit-two" />
      <div className="moon">
        <span />
        <i />
      </div>
      <div className="signal s1" />
      <div className="signal s2" />
      <div className="signal s3" />
      <div className="scene-caption">
        <span>EVERY STORY</span>
        <strong>DESERVES A NEXT CHAPTER.</strong>
      </div>
      <div className="scene-time">
        01:00<span>ONE EPISODE. A NEW WORLD.</span>
      </div>
    </div>
  );
}
